/**
 * @author Mr_FabiozZz[mr.fabiozzz@gmail.com]
 */
import React, { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from "react";
import HeadPage from "pages/StepPages/StepFour/HeadPage";
import sass from "./indicators.module.sass";
import { Button } from "components/shared";
import ModalPortal from "components-new/ModalPortal";
import { BUTTON_VARIANTS } from "components/shared/Button/Button";
import InputTemp from "components-new/InputTemp";
import { useValidate } from "hooks/useValidate";
import DropMenu from "pages/StepPages/StepFour/One/Tables/TableNavigate/DropMenu";
import { Search } from "components-new";
import { ReactComponent as ActionMenuEdit, ReactComponent as EditIcon } from "images/EditorIcon.svg";
import TextArea from "components-new/Textarea";
import { ReactComponent as RemoveIcon } from "images/TrashCan.svg";
import { ReactComponent as AddIcon } from "images/PlusBold.svg";
import { ReactComponent as ReturnIcon } from "images/History.svg";
import { useLocation, useNavigate } from "react-router-dom";
import useDebounce from "hooks/useDebounce";
import Badge from "components-new/Badge";
import { AxiosError } from "axios";
import { snackWrapper } from "helpers";
import { useAppDispatch } from "toolkit/hooks";
import { checkCompletingReferences } from "toolkit/references/references.thunks";
import { directoryAPI } from "libs/apiClient/Directory";
import { SignificanceCriteriaInfo, SignificanceCriteriaSectionInfo } from "libs/apiClient/data-contracts";
import {userAPI} from "../../../libs/apiClient/User";

const initial = {
	firstCategory: "",
	secondCategory: "",
	thirdCategory: "",
	letter: "",
	name: "",
	group: "",
	section: "",
};

interface IModal {
	rootFunc:
		| React.Dispatch<SetStateAction<SignificanceCriteriaSectionInfo[]>>
		| React.Dispatch<SetStateAction<SignificanceCriteriaSectionInfo[] | null>>;
	trigger: IModalTrigger;
	cancelWindow: () => void;
	archive: boolean;
}

interface IModalTrigger {
	active: boolean;
	data:
		| (Omit<SignificanceCriteriaInfo, "group" | "id"> & {
				id?: number | null;
				group: string | number;
				section: string;
				name: string | null | undefined;
		  })
		| null;
	type: "group" | "item" | "groupItem";
	mode: "edit" | "create";
}

const getSignificance = (
	archive: boolean,
	query: string = "",
	func:
		| Dispatch<SetStateAction<SignificanceCriteriaSectionInfo[]>>
		| Dispatch<SetStateAction<SignificanceCriteriaSectionInfo[] | null>>,
) => {
	const params = {
		num: 99999999,
		page: 1,
		query,
	};
	if (archive) {
		directoryAPI.significanceCriteriaListArchiveList(params).then((r) => {
			const data = [...r.data].map((_) => ({
				..._,
				groups: [..._.groups].sort((a, b) => a.num - b.num),
			}));
			func(data);
		});
	} else {
		directoryAPI.significanceCriteriaListList(params).then((r) => {
			const data = [...r.data].map((_) => ({
				..._,
				groups: [..._.groups].sort((a, b) => a.num - b.num),
			}));
			func(data);
		});
	}
};

const createTitle = (type: "group" | "item" | "groupItem", mode: "edit" | "create"): string => {
	switch (mode) {
		case "create":
			switch (type) {
				case "group":
					return "Добавить группу";
				case "groupItem":
					return "Добавить показатель в группу";
				case "item":
					return "Добавить показатель критериев значимости";
			}
			break;
		case "edit":
			switch (type) {
				case "group":
					return "Редактировать группу";
				case "groupItem":
					return "Редактировать показатель в группе";
				case "item":
					return "Редактировать показатель критериев значимости";
			}
			break;
	}
};
const Modal: React.FC<IModal> = ({ trigger, rootFunc, cancelWindow, archive }) => {
	const [newType, setNewType] = useState<
		Omit<SignificanceCriteriaInfo, "group" | "id"> & {
			id?: number | null;
			group: string;
			section: string;
			name: string | null | undefined;
		}
	>(initial);
	const dispatch = useAppDispatch();
	const [errors, setErrors] = useState<string[]>([]);

	const send = () => {
		if (!trigger.data) {
			let letter: string | null = null;
			if (newType.letter) {
				if (/\)/.test(newType.letter)) {
					letter = newType.letter;
				} else {
					letter = newType.letter + ")";
				}
			}
			directoryAPI.significanceCriteriaCreate({
					...newType,
				group:+newType.group,
					letter,
					...(trigger.type !== "group"
						? {
								thirdCategory: newType.thirdCategory,
								secondCategory: newType.secondCategory,
								firstCategory: newType.firstCategory,
						  }
						: {
								thirdCategory: null,
								secondCategory: null,
								firstCategory: null,
						  }),
				})
				.then(() => {
					snackWrapper("success", "Запись успешно создана");
					getSignificance(archive, "", rootFunc);
					cancelWindow();
					dispatch(checkCompletingReferences());
				})
				.catch((error: AxiosError<string[]>) => {
					if (error.response?.status === 409) {
						setErrors(error.response.data);
						setTimeout(() => setErrors([]), 2500);
					}
				});
		} else {
			const { id, ...rest } = newType;
			let letter: string | null = null;
			if (rest.letter) {
				if (/\)/.test(rest.letter)) {
					letter = rest.letter;
				} else {
					letter = rest.letter + ")";
				}
			}
			directoryAPI.significanceCriteriaUpdate(id!, {
					...rest,
				group:+rest.group,
					letter,
					...(trigger.type !== "group"
						? {
								thirdCategory: rest.thirdCategory,
								secondCategory: rest.secondCategory,
								firstCategory: rest.firstCategory,
						  }
						: {
								thirdCategory: null,
								secondCategory: null,
								firstCategory: null,
						  }),
				})
				.then(() => {
					snackWrapper("success", "Запись успешно создана");
					getSignificance(archive, "", rootFunc);
					cancelWindow();
					dispatch(checkCompletingReferences());
				})
				.catch((error) => {
					if (error.status === 409) {
						setErrors(error.data);
						setTimeout(() => setErrors([]), 2500);
					}
				});
		}
	};
	// ?
	// ['name','section','firstCategory','secondCategory','thirdCategory']:

	const { validate, error } = useValidate(
		newType,
		trigger.type === "group"
			? ["name", "section", "group"]
			: trigger.type === "item"
			? ["name", "section", "firstCategory", "secondCategory", "thirdCategory"]
			: ["name", "section", "firstCategory", "secondCategory", "thirdCategory", "letter"],
		send,
	);
	const changeField = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		const { name, value } = e.target;
		setNewType((prevState) => ({ ...prevState, [name]: value }));
	};

	// useEffect(() => {
	//     getSignificance(archive,query,rootFunc)
	//     //eslint-disable-next-line
	// }, [query]);

	useEffect(() => {
		setNewType(trigger.data ? { ...trigger.data, group: String(trigger.data.group) } : initial);
	}, [trigger]);
	return trigger.active ? (
		<ModalPortal title={createTitle(trigger.type, trigger.mode)} outSide={false} handleClose={cancelWindow}>
			<div className={sass.modal}>
				{trigger.type !== "groupItem" && (
					<>
						<InputTemp
							value={newType.section}
							name={"section"}
							error={error && !newType.section}
							onChange={changeField}
							remote={{
								link: "/directory/significance-criteria/section/list",
								initialParams: {
									page: 1,
									num: 20,
								},
								isStringList: true,
							}}
							type={"select"}
							onSelect={(e) => {
								setNewType((prevState) => ({ ...prevState, section: e.name }));
							}}
							field={"name"}
							placeholder={"Введите текст"}
							label={"Раздел"}
							required
						/>
						<InputTemp
							value={newType.group}
							name={"group"}
							errorText={errors.includes("group") ? "Такой номер уже существует" : ""}
							error={error && !newType.group}
							onChange={(e) => {
								e.target.value = e.target.value.replace(/\D/g, "");
								changeField(e);
							}}
							placeholder={"Введите текст"}
							label={"Номер группы"}
							required
						/>
					</>
				)}
				{trigger.type !== "group" && trigger.type !== "item" && (
					<InputTemp
						value={newType.letter || ""}
						name={"letter"}
						onChange={changeField}
						errorText={errors.includes("letter") ? "Такая литера уже существует" : ""}
						error={error && !newType.letter}
						onSelect={(e) => {
							setNewType((prevState) => ({ ...prevState, letter: e.name }));
						}}
						options={[
							{ id: 1, name: "a)" },
							{ id: 2, name: "б)" },
						]}
						field={"name"}
						placeholder={"Введите текст"}
						label={"Литера"}
						required
					/>
				)}
				<TextArea
					value={newType.name}
					name={"name"}
					errorText={errors.includes("name") ? "Такое имя уже существует" : ""}
					error={error && !newType.name}
					onChange={changeField}
					rows={3}
					placeholder={"Введите текст"}
					label={"Показатель"}
					required
				/>

				{trigger.type !== "group" && (
					<>
						<TextArea
							value={newType.thirdCategory}
							name={"thirdCategory"}
							error={error && !newType.thirdCategory}
							onChange={changeField}
							rows={3}
							placeholder={"Введите текст"}
							label={"3 категория"}
							required
						/>
						<TextArea
							value={newType.secondCategory}
							name={"secondCategory"}
							error={error && !newType.secondCategory}
							onChange={changeField}
							rows={3}
							placeholder={"Введите текст"}
							label={"2 категория"}
							required
						/>
						<TextArea
							value={newType.firstCategory}
							name={"firstCategory"}
							error={error && !newType.firstCategory}
							onChange={changeField}
							rows={3}
							placeholder={"Введите текст"}
							label={"1 категория"}
							required
						/>
					</>
				)}

				<div className={sass.btns}>
					<Button onClick={cancelWindow} variant={BUTTON_VARIANTS.OUTLINED}>
						Отменить
					</Button>
					<Button onClick={validate}>Готово</Button>
				</div>
			</div>
		</ModalPortal>
	) : null;
};

const IndicatorsOfSignificance = () => {
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const [isEdit, setIsEdit] = useState(false);

	const location = useLocation();

	const [data, setData] = useState<SignificanceCriteriaSectionInfo[]>([]);

	const [filter, setFilter] = useState<SignificanceCriteriaSectionInfo[] | null>(null);

	const [search, setSearch] = useState("");

	const archive = useMemo(() => {
		return /archive$/g.test(location.pathname);
	}, [location.pathname]);

	const query = useDebounce(search, 400);

	useEffect(() => {
		getSignificance(archive, query, setData);
	}, [archive, query]);

	const [modal, setModal] = useState<IModalTrigger>({ active: false, data: null, type: "item", mode: "create" });
	const [target, setTarget] = useState<HTMLOrSVGElement | undefined>();

	const toggleTarget = (e: React.MouseEvent<HTMLOrSVGElement>) => {
		e.stopPropagation();
		target !== undefined ? setTarget(undefined) : setTarget(e.currentTarget);
	};
	const removeItem = useCallback(
		(otherTarget?: any) => {
			if (!isEdit) {
				needRule();
				return;
			}
			let criteria: { id: number; name: string; [key: string]: any } | undefined;
			if (otherTarget) {
				criteria = { ...otherTarget };
			} else {
				if (target && target.dataset.id) {
					criteria = JSON.parse(target?.dataset.id);
				}
			}
			if (criteria) {
				setTarget(undefined);
				directoryAPI.significanceCriteriaArchiveDetail(criteria.id)
					.then(() => {
						snackWrapper("success", "Запись перенесена в архив");
						getSignificance(archive, query, setData);
						setTarget(undefined);
						dispatch(checkCompletingReferences());
					})
					.catch(() => {
						setTarget(undefined);
					});
			}
		},
		//eslint-disable-next-line
		[isEdit, target],
	);
	const menuRef = useRef<any>(null);

	const restoreFromArchive: typeof removeItem = useCallback(
		(otherTarget: any) => {
			if (!isEdit) {
				needRule();
				return;
			}
			let criteria: { id: number; name: string; [key: string]: any } | undefined;
			if (otherTarget) {
				criteria = { ...otherTarget };
			} else {
				if (target && target.dataset.id) {
					criteria = JSON.parse(target?.dataset.id);
				}
			}
			if (criteria) {
				directoryAPI.significanceCriteriaArchiveDelete(criteria.id)
					.then(() => {
						snackWrapper("success", "Запись восстановлена");
						getSignificance(archive, query, setData);
						setTarget(undefined);
					})
					.catch((err: AxiosError<string[]>) => {
						if (err.response?.status === 409) {
							if (err.response?.data.includes("group")) {
								snackWrapper(
									"warn",
									`Номер группы (${criteria?.group}) в секции ${criteria?.section} присутствует в основном списке`,
								);
							}
						}
						setTarget(undefined);
					});
			}
		},
		//eslint-disable-next-line
		[isEdit],
	);

	useEffect(() => {
		function handleClickOutside(event: any) {
			if (menuRef.current && target && !menuRef.current.contains(event.target)) {
				setTarget(undefined);
			}
		}

		document.addEventListener("mouseup", handleClickOutside);
		return () => {
			document.removeEventListener("mouseup", handleClickOutside);
		};
	}, [target]);
	const outlineStyle = useCallback(
		(id: number) => {
			return location.state && location.state?.outline && location.state?.outline === id ? sass.outline : "";
		},
		//eslint-disable-next-line
		[location.state?.outline],
	);

	const needRule = () => {
		snackWrapper("error", "Не достаточно прав");
	};
	const [disabled, setDisabled] = useState(false);

	useEffect(() => {
		userAPI.permissionsCheckCreate(0, [
				"dictionary.significance_criteria.edit",
				"dictionary.inapplicability_reason.edit",
			])
			.then(({ data }) => {
				setIsEdit(data.includes("dictionary.significance_criteria.edit"));
				setDisabled(!data.includes("dictionary.inapplicability_reason.edit"));
			});
	}, []);

	return (
		<div className={sass.wrapper}>
			{archive ? (
				<DropMenu clear={true} ref={menuRef} target={target} click={removeItem}>
					<div className={sass.field} onClick={() => restoreFromArchive()}>
						<ReturnIcon width={16} height={16} className={sass.success} />
						Восстановить
					</div>
				</DropMenu>
			) : (
				<DropMenu clear={true} ref={menuRef} target={target} click={removeItem}>
					<div
						className={sass.field}
						onClick={() => {
							if (!isEdit) {
								needRule();
								return;
							}
							let criteria;

							if (target && target.dataset.id) {
								criteria = JSON.parse(target?.dataset.id);
							}
							if (criteria) {
								setModal({
									data: criteria,
									mode: "edit",
									type: "groupItem",
									active: true,
								});
							}
						}}>
						<ActionMenuEdit width={16} height={16} className={sass.edit} />
						Редактировать
					</div>
					<div className={sass.field} onClick={() => (disabled ? needRule() : removeItem())}>
						<RemoveIcon width={16} />
						Переместить в архив
					</div>
				</DropMenu>
			)}
			<Modal
				archive={archive}
				rootFunc={filter && filter.length ? setFilter : setData}
				trigger={modal}
				cancelWindow={() => setModal((prevState) => ({ ...prevState, active: false }))}
			/>
			<HeadPage
				title={
					archive ? (
						<div className={sass.headTitle}>
							Архив показателей критериев значимости
							<Badge color={"#7CADAA"}>Архив</Badge>
						</div>
					) : (
						"Показатели критериев значимости"
					)
				}>
				<div className={sass.head}>
					<Search className={sass.search} searchValue={search} handleSearch={(e) => setSearch(e.target.value)} />
					{!archive && (
						<>
							<Button
								onClick={() => navigate(location.pathname + "/archive", { state: location.state })}
								variant={BUTTON_VARIANTS.OUTLINED}>
								Перейти в архив
							</Button>
							<Button
								disabled={!isEdit || disabled}
								variant={BUTTON_VARIANTS.OUTLINED}
								onClick={() => setModal({ active: true, data: null, type: "item", mode: "create" })}>
								Добавить показатель
							</Button>
							<Button
								disabled={!isEdit || disabled}
								onClick={() => setModal({ active: true, data: null, type: "group", mode: "create" })}>
								Добавить группу
							</Button>
						</>
					)}
				</div>
			</HeadPage>
			<table>
				<thead>
					<tr>
						<th>№</th>
						<th>Показатель</th>
						<th>3 категория</th>
						<th>2 категория</th>
						<th>1 категория</th>
						<th />
					</tr>
				</thead>
			</table>
			<div data-comment-false={true} className={sass.scroll}>
				{data.map((section, indexSection) => {
					return (
						<React.Fragment key={indexSection}>
							<div className={sass.tableCaption}>
								{indexSection + 1} {section.name}
							</div>
							<table>
								<tbody>
									{section.groups.map((group, indexGroup) => {
										return (
											<React.Fragment key={indexGroup}>
												{!group.id ? (
													group.criterias.map((criterias) => {
														return (
															<tr
																className={outlineStyle(group.id!)}
																key={criterias.id}
																onClick={() => {
																	if (!isEdit) {
																		needRule();
																		return;
																	}
																	!archive &&
																		setModal({
																			active: true,
																			data: {
																				...criterias,
																				section: section.name,
																				group: group.num,
																			},
																			type: "item",
																			mode: "edit",
																		});
																}}>
																<td>{group.num}</td>
																<td>
																	{criterias.letter ? criterias.letter + " " : ""}
																	{criterias.name}
																</td>
																<td>{criterias.thirdCategory}</td>
																<td>{criterias.secondCategory}</td>
																<td>{criterias.firstCategory}</td>
																<td>
																	<div
																		className={`dotted-icon ${sass.dotted}`}
																		data-id={JSON.stringify({
																			...criterias,
																			section: section.name,
																			group: group.num,
																		})}
																		onClick={toggleTarget}
																	/>
																</td>
															</tr>
														);
													})
												) : (
													<>
														<tr key={group.id} className={`${sass.noHover} ${outlineStyle(group.id)}`}>
															<td className={sass.colspan}>{group.num}</td>
															<td className={sass.colspan}>{group.name}</td>
															<td className={sass.colspan} colSpan={4}>
																<div className={sass.btn_groups}>
																	{archive ? (
																		<>
																			<Button
																				variant={BUTTON_VARIANTS.TEXT}
																				disabled={!isEdit}
																				onClick={() => {
																					restoreFromArchive({
																						id: group.id,
																						section: section.name,
																						group: group.num,
																						name: group.name,
																					});
																				}}>
																				<ReturnIcon className={sass.success} width={14} height={14} />
																				Восстановить
																			</Button>
																		</>
																	) : (
																		<>
																			<Button
																				variant={BUTTON_VARIANTS.TEXT}
																				disabled={!isEdit}
																				onClick={() => {
																					setModal({
																						active: true,
																						data: {
																							...initial,
																							id: group.id,
																							section: section.name,
																							group: group.num,
																							name: "",
																						},
																						type: "groupItem",
																						mode: "create",
																					});
																				}}>
																				<AddIcon className={sass.infoIcon} width={14} height={14} />
																				Добавить
																			</Button>
																			<Button
																				variant={BUTTON_VARIANTS.TEXT}
																				disabled={!isEdit}
																				onClick={() => {
																					setModal({
																						active: true,
																						data: {
																							...initial,
																							id: group.id,
																							section: section.name,
																							group: group.num,
																							name: group.name as any,
																						},
																						type: "group",
																						mode: "edit",
																					});
																				}}>
																				<EditIcon className={sass.infoIcon} width={14} height={14} />
																				Редактировать
																			</Button>

																			<Button
																				variant={BUTTON_VARIANTS.TEXT}
																				disabled={!isEdit}
																				onClick={() => {
																					removeItem({
																						id: group.id,
																						section: section.name,
																						group: group.num,
																						name: group.name,
																					});
																				}}>
																				<RemoveIcon className={sass.danger} height={14} />
																				Поместить в архив
																			</Button>
																		</>
																	)}
																</div>
															</td>
														</tr>
														{group.criterias.map((criterias) => {
															return (
																<tr
																	className={outlineStyle(group.id!)}
																	key={criterias.id}
																	onClick={() => {
																		if (!isEdit) {
																			needRule();
																			return;
																		}
																		!archive &&
																			setModal({
																				active: true,
																				data: {
																					...criterias,
																					section: section.name,
																					group: group.num,
																				},
																				type: "groupItem",
																				mode: "edit",
																			});
																	}}>
																	<td />
																	<td>
																		{criterias.letter ? criterias.letter + " " : ""}
																		{criterias.name}
																	</td>
																	<td>{criterias.thirdCategory}</td>
																	<td>{criterias.secondCategory}</td>
																	<td>{criterias.firstCategory}</td>
																	<td>
																		<div
																			className={`dotted-icon ${sass.dotted}`}
																			data-id={JSON.stringify({
																				...criterias,
																				id: criterias.id,
																				section: section.name,
																				group: group.num,
																				name: group.name,
																			})}
																			onClick={toggleTarget}
																		/>
																	</td>
																</tr>
															);
														})}
													</>
												)}
											</React.Fragment>
										);
									})}
								</tbody>
							</table>
						</React.Fragment>
					);
				})}
			</div>
		</div>
	);
};

export default IndicatorsOfSignificance;
